import { z } from "zod";
import apiClient from "../../../shared/api/client";
import type {
  Paginated,
  SupportReport,
  SupportReportsQuery,
  UpdateSupportReportPatch,
} from "../types";

const jsonRecord = z.record(z.string(), z.unknown()).nullable();

const noteSchema = z.object({
  authorId: z.string(),
  text: z.string(),
  at: z.string(),
});

const attachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  sizeBytes: z.number(),
  kind: z.enum([
    "eriol-log",
    "gstreamer-log",
    "cef-log",
    "kero-log",
    "renderer-log",
    "screenshot",
  ]),
});

const reportSchema = z.object({
  id: z.string(),
  userId: z.number().nullable(),
  userSnapshot: z
    .object({
      email: z.string().nullable(),
      username: z.string().nullable(),
      name: z.string().nullable(),
    })
    .nullable(),
  source: z.enum(["about", "menu-bar", "error-boundary", "mobile", "web"]),
  category: z.string().nullable(),
  message: z.string(),
  status: z.enum(["new", "open", "pending", "resolved", "closed"]),
  severity: z.string().nullable(),
  assignedTo: z.string().nullable(),
  appVersion: z.string().nullable(),
  platform: z.string().nullable(),
  osVersion: z.string().nullable(),
  arch: z.string().nullable(),
  diagnostics: jsonRecord,
  attachments: z.array(attachmentSchema),
  relatedErrorGroupId: z.string().nullable(),
  internalNotes: z.array(noteSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const paginatedReports = z.object({
  data: z.array(reportSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

function toParams(query: object): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") params[k] = String(v);
  }
  return params;
}

export const fetchSupportReports = async (
  query: SupportReportsQuery,
): Promise<Paginated<SupportReport>> => {
  const res = await apiClient.get("/admin/support/reports", {
    params: toParams(query),
  });
  return paginatedReports.parse(res.data);
};

export const fetchSupportReport = async (
  id: string,
): Promise<SupportReport> => {
  const res = await apiClient.get(`/admin/support/reports/${id}`);
  return reportSchema.parse(res.data);
};

export const updateSupportReport = async (
  id: string,
  patch: UpdateSupportReportPatch,
): Promise<SupportReport> => {
  const res = await apiClient.patch(`/admin/support/reports/${id}`, patch);
  return reportSchema.parse(res.data);
};

export const addSupportNote = async (
  id: string,
  text: string,
): Promise<SupportReport> => {
  const res = await apiClient.post(`/admin/support/reports/${id}/notes`, {
    text,
  });
  return reportSchema.parse(res.data);
};

/**
 * Fetch a private attachment as an object URL (for inline `<img>` preview of
 * screenshots). Caller is responsible for `URL.revokeObjectURL` when done.
 */
export const fetchSupportAttachmentObjectUrl = async (
  reportId: string,
  attId: string,
): Promise<string> => {
  const res = await apiClient.get(
    `/admin/support/reports/${reportId}/attachments/${attId}`,
    { responseType: "blob" },
  );
  return URL.createObjectURL(res.data as Blob);
};

/** Download a private attachment through the authenticated proxy. */
export const downloadSupportAttachment = async (
  reportId: string,
  attId: string,
  filename: string,
): Promise<void> => {
  const res = await apiClient.get(
    `/admin/support/reports/${reportId}/attachments/${attId}`,
    { responseType: "blob" },
  );
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
